import { useEffect, useRef, useState } from 'react';

import { IStore } from '@rbi-ctg/store';
import useStoreLocator from 'pages/store-locator/hooks/use-store-locator';
import { useGeolocation } from 'state/geolocation';
import { useLocale } from 'state/intl';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { ServiceMode, useServiceModeContext } from 'state/service-mode';
import { useStoreContext } from 'state/store';
import { isMobile } from 'utils/environment';
import {
  GeolocationPermissionStates,
  determinePermissionsFromGetCurrentPositionErrorCode,
  getUsersCurrentPosition
} from 'utils/geolocation';

/**
 *
 * Auto selects closest restaurant based on navigator geolocation
 *
 **/
export const useAutoSelectRestaurant = () => {
  const [restaurantToConfirm, setRestaurantToConfirm] = useState<IStore | null>(null);
  const checkedClosestStore = useRef(false);
  const { setStore, store } = useStoreContext();
  const { setServiceMode } = useServiceModeContext();
  const { permission, setPermission } = useGeolocation();
  const { determiningLocaleComplete } = useLocale();
  const enableAutoLocale = useFlag(LaunchDarklyFlag.ENABLE_AUTO_LOCALE);
  const noStoreSelectedOnMount = useRef(!store.hasSelection);
  // This ensures that autoSelectRestaurant hook does not run prior to correct domain / locale being selected
  const autoLocaleComplete = !enableAutoLocale || determiningLocaleComplete;
  const notMobileSizedDevice = !isMobile();
  // The storeLocater hook fetches user geolocation and queries closest stores on mount if
  // permission.isGranted and listens for changes in permission status.
  // loadPositionOnMount is set to true so we can determine if closest store has changed since last session.
  const { nearbyStores } = useStoreLocator({
    loadPositionOnMount: autoLocaleComplete,
    onStoreLocator: false
  });

  // Only use native prompt on mount if not mobile sized device (including mobile running web app)
  // and permission for geolocation unknown

  const confirmRestaurant = (confirmed: boolean) => {
    if (restaurantToConfirm && confirmed) {
      setStore({ ...restaurantToConfirm, hasSelection: true });
      setServiceMode(ServiceMode.TAKEOUT);
    }
    setRestaurantToConfirm(null);
  };

  useEffect(() => {
    if (
      permission === GeolocationPermissionStates.PROMPT &&
      notMobileSizedDevice &&
      noStoreSelectedOnMount.current &&
      autoLocaleComplete
    ) {
      // promptUserForGeoPermission is only to prompt for permission through native api
      const promptUserForGeoPermission = async () => {
        try {
          await getUsersCurrentPosition();
          setPermission(GeolocationPermissionStates.GRANTED);
        } catch (e) {
          if (e?.code) {
            setPermission(determinePermissionsFromGetCurrentPositionErrorCode(e.code));
          }
        }
      };

      promptUserForGeoPermission();
    }
  }, [autoLocaleComplete, notMobileSizedDevice, permission, setPermission]);

  // If nearby stores available from useStoreLocater hook, select first available
  // And if closest store has changed since last session, select first available from new nearby stores
  // (results for nearby stores limited to only open)
  useEffect(() => {
    const hasClosestStoreChanged = () => {
      // This function should only run once
      if (checkedClosestStore.current) {
        return false;
      }
      checkedClosestStore.current = true;
      return nearbyStores && store.hasSelection && store?._id !== nearbyStores[0]?._id;
    };

    if (nearbyStores?.length && (!store.hasSelection || hasClosestStoreChanged())) {
      setRestaurantToConfirm(nearbyStores[0]);
    }
  }, [store, nearbyStores]);

  return { confirmRestaurant, restaurantToConfirm };
};
